/**
 * Décisions du Conseil constitutionnel — recherche et décisions récentes
 * Route : /conseil-constitutionnel
 */
import { getDateLocale } from '@/i18n';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Loader } from '@/components/Loader';
import { PublicFooter, PublicHeader } from '@/components/jurisprudence';

export interface ConstitSummary {
  id: string;
  numero: string;
  titre: string;
  date: string;
  nature: string;
  solution: string;
  ecli: string;
  extrait: string;
  score?: number | null;
}

// « 2010-613 » + « DC » → « 2010-613-dc » ; « 58-81/84 » → « 58-81_84-an » (le backend relit ce format)
export const decisionSlug = (numero: string, nature?: string) =>
  [numero.trim().replace(/\//g, '_'), nature]
    .filter(Boolean)
    .join('-')
    .toLowerCase();

export function formatDate(d?: string): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(getDateLocale(), {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return d;
  }
}

export function NatureBadge({ nature }: { nature?: string }) {
  if (!nature) return null;
  const colors: Record<string, string> = {
    QPC: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    DC: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    L: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
  };
  return (
    <span
      className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
        colors[nature] ??
        'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
      }`}
    >
      {nature}
    </span>
  );
}

const NATURE_FILTERS = [
  { value: '', key: 'constit.filters.all' },
  { value: 'QPC', key: 'constit.filters.qpc' },
  { value: 'DC', key: 'constit.filters.dc' },
  { value: 'L', key: 'constit.filters.l' }
];

function DecisionCard({ d }: { d: ConstitSummary }) {
  return (
    <Link
      to={`/conseil-constitutionnel/${decisionSlug(d.numero, d.nature)}`}
      className="block bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-md transition-all"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-xs font-mono text-violet-600 dark:text-violet-400 font-semibold">
          {d.numero}
        </span>
        <NatureBadge nature={d.nature} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {formatDate(d.date)}
        </span>
      </div>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 leading-snug">
        {d.titre}
      </h3>
      {d.solution && (
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {d.solution}
        </p>
      )}
    </Link>
  );
}

export default function PublicConstit() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const nature = params.get('nature') || '';
  const [input, setInput] = useState(q);
  const [results, setResults] = useState<ConstitSummary[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    setResults(null);
    const qs = new URLSearchParams({ limit: '20' });
    if (nature) qs.set('nature', nature);
    const url = q
      ? `/api/constit/search?${qs}&q=${encodeURIComponent(q)}`
      : `/api/constit/recent?${qs}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((d) => {
        setUnavailable(d.mode === 'unavailable');
        setResults(d.results ?? []);
      })
      .catch(() => setResults([]));
  }, [q, nature]);

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k)
    );
    setParams(next);
  };

  return (
    <>
      <Helmet>
        <title>{t('constit.meta.title')}</title>
        <meta name="description" content={t('constit.meta.description')} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        <PublicHeader />
        <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('constit.hero.title')}
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {t('constit.hero.subtitle')}
          </p>

          <form
            className="mt-6 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              update({ q: input.trim() });
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('constit.search.placeholder')}
              className="flex-1 text-sm border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
            <button
              type="submit"
              className="px-5 rounded-xl bg-violet-600 text-white font-medium hover:bg-violet-700"
            >
              {t('constit.search.button')}
            </button>
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {NATURE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => update({ nature: f.value })}
                className={`text-xs px-3 py-1.5 rounded-full border ${
                  nature === f.value
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                {t(f.key)}
              </button>
            ))}
            {q && (
              <button
                onClick={() => {
                  setInput('');
                  update({ q: '' });
                }}
                className="text-xs px-3 py-1.5 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {t('constit.search.clear')}
              </button>
            )}
          </div>

          <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            {q ? t('constit.results') : t('constit.recent')}
          </h2>
          {results === null ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : unavailable ? (
            <p className="text-gray-500">{t('constit.unavailable')}</p>
          ) : results.length === 0 ? (
            <p className="text-gray-500">{t('constit.empty')}</p>
          ) : (
            <div className="grid gap-3">
              {results.map((d) => (
                <DecisionCard key={d.id} d={d} />
              ))}
            </div>
          )}
        </main>
        <PublicFooter />
      </div>
    </>
  );
}
